<template>
    <component :is="tag"
               class="ui-button"
               :class="classes"
               :disabled="disabled"
               v-bind="buttonAttributes"
               aria-label="button"
               @click="handleClick"
               @mouseenter="handleMouseEnter"
               @mouseleave="handleMouseLeave"
    >
        <WBIcon v-if="icon && iconPosition === 'left'" class="ui-button__icon _left" :name="icon" />

        <div v-if="$slots.default" class="ui-button__label">
            <slot />
        </div>

        <WBIcon v-if="icon && iconPosition === 'right'" class="ui-button__icon _right" :name="icon" />
    </component>
</template>

<script setup lang="ts">
    import type { UiButtonEmits, UiButtonProps } from './types/types'
    import { useAttrs } from 'vue'
    import { useButton } from '~/src/shared/components/ui/button/composable/useButton'

    const props = withDefaults(defineProps<UiButtonProps>(), {
        tag: 'button',
        href: '',
        to: '',
        disabled: false,
        color: 'primary',
        iconPosition: 'left',
    })

    const emits = defineEmits<UiButtonEmits>()
    const attrs = useAttrs()
    const { tag, classes, buttonAttributes, handleClick, handleMouseEnter, handleMouseLeave } = useButton(
        props,
        emits,
        attrs
    )
</script>

<style lang="scss">
    .ui-button {
        $button: &;

        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px 28px;
        border-radius: 30px;
        outline: none;
        transition:
            color $default-transition,
            opacity $default-transition,
            background-color $default-transition,
            border-color $default-transition,
            box-shadow $default-transition;
        cursor: pointer;
        user-select: none;

        &__label {
            @include manrope16m;

            transition:
                color $default-transition,
                opacity $default-transition,
                background-color $default-transition,
                border-color $default-transition;
        }

        &._primary {
            border: 1px solid var(--blue-600);
            background-color: var(--blue-600);
            box-shadow: 4px 4px 12px 0 #0057ff40;
            color: var(--white);

            @include hover {
                border-color: var(--blue-500);
                background-color: var(--blue-500);
                box-shadow: 4px 4px 12px 0 #0057ff40;
            }

            &._disabled {
                border: 1px solid var(--disable-bg);
                background-color: var(--disable-bg);
                box-shadow: none;
                color: var(--blue-300);

                @include hover {
                    border: 1px solid var(--disable-bg);
                    background-color: var(--disable-bg);
                }
            }
        }

        &._primary-white {
            border: 1px solid var(--white);
            background-color: var(--white);
            box-shadow: 4px 4px 12px 0 #0057ff40;
            color: var(--blue-600);

            @include hover {
                border-color: var(--blue-500);
                color: var(--blue-500);
            }

            &._disabled {
                border: 1px solid var(--disable-bg);
                background-color: var(--disable-bg);
                box-shadow: none;
                color: var(--blue-300);
            }
        }

        &._secondary {
            border-radius: 30px;
            border: 1px solid var(--blue-600);
            background-color: var(--white);
            color: var(--blue-600);

            @include hover {
                border-color: var(--blue-500);
                background-color: var(--blue-50);
                color: var(--blue-500);
            }

            &._disabled {
                border: 1px solid var(--blue-300);
                color: var(--blue-300);
            }
        }

        &._secondary-white {
            border-radius: 30px;
            border: 1px solid var(--white);
            color: var(--white);

            @include hover {
                border-color: var(--blue-500);
                background-color: var(--white);
                color: var(--blue-500);
            }

            &._disabled {
                border: 1px solid var(--white);
                color: var(--white);
            }
        }

        &._tertiary {
            padding: 8px 24px;
            border-radius: 4px;
            background-color: var(--indigo-50);
            color: var(--blue-600);

            @include hover {
                background-color: var(--blue-50);
            }

            &._disabled {
                color: var(--blue-300);
            }

            &._active {
                background-color: var(--blue-50);
            }
        }

        &._actions {
            background-color: #F1F6FC;
        }

        &._disabled {
            pointer-events: none;
        }

        &._loading {
            pointer-events: none;

            #{$button}__label,
            #{$button}__icon {
                opacity: 0;
            }

            &:before {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                width: 1em;
                border-radius: 50%;
                border: 0.2em solid;
                border-color: #000 #0000;
                transform: translate(-50%, -50%) rotate(0.5turn);
                animation: l1 1s infinite;
                aspect-ratio: 1;
            }
        }

        &._icon-left {
            padding-left: 20px;
        }

        &._icon-right {
            padding-right: 20px;
        }

        &__icon {
            font-size: 20px;
            color: currentcolor;

            &._left {
                margin-right: 8px;
            }

            &._right {
                margin-left: 8px;
            }
        }
    }
</style>
